/**
 * @voltxampere/web — Edit category builtin commands (Sprint 22 C3)
 *
 * Sprint 80.Z Phase 4 D1 — palette stub cleanup. 6 clipboard /
 * transform commands (copy, cut, paste, duplicate, flip-h, flip-v)
 * removed from the catalog because they were noop handlers that
 * misled users — Cmd+C / Cmd+X / Cmd+V opened in the palette
 * advertised the shortcut but did nothing. They re-land when the
 * clipboard + transform pipeline ships (tracked separately).
 *
 * Remaining 8 commands: undo/redo (wired via undoStack), delete /
 * select-all / deselect / rotate / rotate-cw / rotate-ccw — the
 * delete + rotate variants are **discovery aliases** documented
 * inline: the canvas keyboard handler executes the action, the
 * palette entry exposes the shortcut for users who don't know it.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import type { Command } from "../types.js";
import { currentCircuit } from "../../store/circuit.js";
import { undoStack } from "../../store/undo-stack.js";

const noop = (): void => {
  /* Discovery alias — canvas/keyboard handler owns the real action. */
};

export const EDIT_COMMANDS: readonly Command[] = [
  {
    id: "edit.undo",
    titleKey: "command.edit.undo",
    descriptionKey: "command.edit.undo.desc",
    category: "edit",
    shortcut: "Cmd+Z",
    when: () => undoStack.canUndo,
    execute: () => {
      const c = currentCircuit.value;
      if (!c) return;
      const next = undoStack.undo(c);
      if (next) currentCircuit.value = next;
    },
  },
  {
    id: "edit.redo",
    titleKey: "command.edit.redo",
    descriptionKey: "command.edit.redo.desc",
    category: "edit",
    shortcut: "Cmd+Shift+Z",
    when: () => undoStack.canRedo,
    execute: () => {
      const c = currentCircuit.value;
      if (!c) return;
      const next = undoStack.redo(c);
      if (next) currentCircuit.value = next;
    },
  },
  // Sprint 80.Z Phase 4 D1 — `edit.copy` / `edit.cut` / `edit.paste`
  // / `edit.duplicate` removed (clipboard not wired; re-land with
  // the clipboard pipeline). `edit.flip-h` / `edit.flip-v` removed
  // (transform not implemented).
  {
    id: "edit.delete",
    titleKey: "command.edit.delete",
    category: "edit",
    shortcut: "Delete",
    execute: noop, // canvas already handles via keyboard; palette is a discovery alias
  },
  {
    id: "edit.select-all",
    titleKey: "command.edit.selectAll",
    category: "edit",
    shortcut: "Cmd+A",
    execute: noop,
  },
  {
    id: "edit.deselect",
    titleKey: "command.edit.deselect",
    category: "edit",
    shortcut: "Esc",
    execute: noop,
  },
  {
    id: "edit.rotate",
    titleKey: "command.edit.rotate",
    descriptionKey: "command.edit.rotate.desc",
    category: "edit",
    shortcut: "Shift+R",
    execute: noop, // canvas handles via keyboard; palette is a discovery alias
  },
  {
    id: "edit.rotate-cw",
    titleKey: "command.edit.rotateCw",
    category: "edit",
    execute: noop,
  },
  {
    id: "edit.rotate-ccw",
    titleKey: "command.edit.rotateCcw",
    category: "edit",
    execute: noop,
  },
];
